<template>
  <BaseSlideOverModal
    v-model:is-modal-open="localOpen"
    width="xl"
    :title="$t('navigation.template_dashboard_title_main')"
    @close="close"
  >
    <template #body>
      <div class="flex h-full flex-1 flex-col gap-4 overflow-y-auto">
        <div class="flex flex-col gap-2">
          <div
            v-if="canManageFavoriteMembership"
            class="flex flex-row items-center gap-2"
          >
            <div class="shrink-0">
              <FontAwesomeIcon
                :icon="['fas', 'star']"
                fixed-width
                class="text-yellow-300"
                size="lg"
              />
            </div>
            <div class="flex-1">
              <h2 class="text-lg font-semibold capitalize-first">
                {{ $t('attributes.starred') }}
              </h2>
            </div>
          </div>
          <div class="rounded border">
            <div v-if="!hasOrderedDashboards" class="p-2">
              {{ $t('global.click_add_button') }}
            </div>
            <template v-for="section in orderedSections" :key="section.key">
              <h3
                v-if="section.title && section.items.length"
                class="border-b bg-gray-50 px-2 py-1 text-xs font-semibold tracking-wide text-gray-500 uppercase dark:bg-slate-700 dark:text-gray-300"
              >
                {{ section.title }}
              </h3>
              <draggable
                v-model="section.items"
                item-key="id"
                class="divide-y divide-gray-100"
                @update="saveOrder"
              >
                <template #item="{ element }">
                  <div class="flex cursor-move flex-row items-center gap-2 p-2">
                    <div class="shrink-0">
                      <FontAwesomeIcon
                        :icon="['fal', 'bars']"
                        fixed-width
                        class="cursor-pointer text-primary"
                      />
                    </div>
                    <div class="flex-1">
                      <h3
                        class="text-sm leading-6 font-semibold text-gray-900 dark:border-white dark:bg-slate-700 dark:text-white"
                      >
                        {{ element.name[$i18n.locale] }}
                      </h3>
                    </div>
                    <div class="shrink-0">
                      <button
                        v-if="canManageFavoriteMembership"
                        @click="removeFavorite(element)"
                      >
                        <FontAwesomeIcon
                          :icon="['fal', 'times-circle']"
                          class="text-theme-danger"
                          fixed-width
                        />
                      </button>
                    </div>
                  </div>
                </template>
              </draggable>
            </template>
          </div>
        </div>

        <div v-if="canManageFavoriteMembership" class="flex flex-col gap-2">
          <div class="flex flex-row items-center gap-2">
            <div class="shrink-0">
              <FontAwesomeIcon :icon="['fal', 'star']" fixed-width size="lg" />
            </div>
            <div class="flex-1">
              <h2 class="text-lg font-semibold capitalize-first">
                {{ $t('global.others') }}
              </h2>
            </div>
          </div>
          <div
            class="divide-y divide-gray-100 rounded border dark:divide-white"
          >
            <div v-for="template in otherUserDashboards" :key="template.id">
              <div class="flex flex-row items-center gap-2 p-2">
                <div class="flex-1">
                  <h3 class="text-sm leading-6 font-semibold text-gray-900">
                    {{ template.name[$i18n.locale] }}
                  </h3>
                </div>
                <div class="shrink-0">
                  <button @click="addFavorite(template)">
                    <FontAwesomeIcon
                      :icon="['fal', 'plus-circle']"
                      class="text-primary"
                      fixed-width
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </BaseSlideOverModal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BaseSlideOverModal from '@c/addf-package/components/modal/BaseSlideOverModal.vue'
import draggable from 'vuedraggable'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import DashboardTypes from '@/assets/enums/dashboard/DashboardTypes'
import { compareByPosition } from '@u/helpers/dashboardOrder'

export default {
  name: 'DashboardModal',
  components: {
    FontAwesomeIcon,
    BaseSlideOverModal,
    draggable
  },
  props: {
    open: {
      type: Boolean,
      required: true
    },
    typeDashboardAvailable: {
      type: Array,
      required: false,
      default: () => Object.values(DashboardTypes)
    }
  },
  emits: ['close', 'status-dashboard-change', 'update:open'],
  data() {
    return {
      /**
       * Local, draggable copy of the ordered dashboards: vuedraggable writes the new
       * order here; the metadata refresh after saving re-syncs it (BBSW-11320).
       * @type {{ key: string, title: string|null, items: object[] }[]}
       */
      orderedSections: []
    }
  },
  computed: {
    ...mapGetters({
      metadata: 'metadata/getInitialMetadata',
      templateDashboard: 'metadata/getTemplateDashboards'
    }),
    localOpen: {
      get() {
        return this.open
      },
      set(value) {
        this.$emit('update:open', value)
      }
    },
    canManageFavorite() {
      return this.typeDashboardAvailable.length > 1
    },
    /**
     * Adding or removing a starred dashboard writes a pivot row through
     * `template_dashboard_user.update`, which stays behind `manage_dashboards`
     * (BBSW-6533). A holder of `organize_dashboards` alone reorders their tabs
     * but never sees those controls, instead of meeting a 403 on click.
     *
     * Kept separate from `canManageFavorite`: that one also decides which
     * position column the drawer saves into, and must not depend on a permission.
     * @returns {boolean}
     */
    canManageFavoriteMembership() {
      return this.canManageFavorite && this.$can('manage_dashboards')
    },
    favoriteUserDashboards() {
      const dashboards = this.metadata?.models?.template_dashboards ?? []

      if (this.typeDashboardAvailable.length === 1) {
        return dashboards
          .filter((dashboard) =>
            this.typeDashboardAvailable.includes(dashboard.type)
          )
          .sort(compareByPosition('local_position'))
      }

      return dashboards
        .filter(
          (dashboard) =>
            dashboard.pivot.favorite === 1 || dashboard.pivot.favorite === true
        )
        .sort(compareByPosition('position'))
    },
    /**
     * The statistics tabs bar shows predefined views before "my views": the drawer
     * mirrors those two sections so the saved order is the displayed one.
     * @returns {boolean}
     */
    hasPredefinedSection() {
      return (
        !this.canManageFavorite &&
        this.typeDashboardAvailable.includes(DashboardTypes.ANALYTICS)
      )
    },
    /**
     * @returns {{ key: string, title: string|null, items: object[] }[]}
     */
    sourceSections() {
      if (!this.hasPredefinedSection) {
        return [{ key: 'all', title: null, items: this.favoriteUserDashboards }]
      }

      return [
        {
          key: 'predefined',
          title: this.$t('global.predefined_views'),
          items: this.favoriteUserDashboards.filter(
            (dashboard) => dashboard.is_default
          )
        },
        {
          key: 'custom',
          title: this.$t('global.my_views'),
          items: this.favoriteUserDashboards.filter(
            (dashboard) => !dashboard.is_default
          )
        }
      ]
    },
    hasOrderedDashboards() {
      return this.orderedSections.some((section) => section.items.length > 0)
    },
    otherUserDashboards() {
      return this.metadata?.models?.template_dashboards
        ?.filter(
          (dashboard) =>
            dashboard.pivot.favorite === 0 ||
            dashboard.pivot.favorite === false ||
            dashboard.pivot.favorite == null
        )
        ?.sort((a, b) => {
          if (a.pivot.position === null || b.pivot.position === null) {
            return a.pivot.position === null ? 1 : -1
          }

          return a.pivot.position - b.pivot.position
        })
    }
  },
  watch: {
    sourceSections: {
      handler(sections) {
        this.orderedSections = sections.map((section) => ({
          ...section,
          items: [...section.items]
        }))
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions({
      processUpdateTemplateDashboardUserByIdAction:
        'dashboard/updateTemplateDashboardUserById',
      processRefreshSpecificInitialMetadataModelsAction:
        'metadata/refreshSpecificInitialMetadataModels',
      processUpdateTemplateDashboardUserPositionAction:
        'dashboard/updateTemplateDashboardUserPosition'
    }),
    close() {
      this.$emit('close')
    },
    /**
     * Sends the whole displayed order (ids), never a moved index: predefined
     * dashboards may have no pivot row server-side, so indexes cannot be replayed.
     */
    saveOrder() {
      this.processUpdateTemplateDashboardUserPositionAction({
        ordered_ids: this.orderedSections.flatMap((section) =>
          section.items.map((dashboard) => dashboard.id)
        ),
        only_favorite: this.canManageFavorite,
        dashboard_type: this.canManageFavorite
          ? null
          : this.typeDashboardAvailable[0]
      })
        .then(() => {
          this.processRefreshSpecificInitialMetadataModelsAction({
            model: 'template_dashboards'
          })
        })
        .catch(() => {
          // Back to the saved order: the drawer must not show an order that was not stored.
          this.orderedSections = this.sourceSections.map((section) => ({
            ...section,
            items: [...section.items]
          }))
          this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
        })
    },
    removeFavorite(element) {
      this.updateFavoriteStatus(element, false)
    },

    addFavorite(element) {
      this.updateFavoriteStatus(element, true)
    },

    updateFavoriteStatus(element, favoriteStatus) {
      const newFavoriteStatus = favoriteStatus ? 1 : 0

      this.processUpdateTemplateDashboardUserByIdAction({
        id: element.pivot.id,
        data: {
          template_dashboard_id: element.pivot.template_dashboard_id,
          user_id: element.pivot.user_id,
          position: element.pivot.position,
          favorite: newFavoriteStatus
        }
      }).then(() => {
        this.processRefreshSpecificInitialMetadataModelsAction({
          model: 'template_dashboards'
        })

        if (favoriteStatus) {
          this.$router.push({
            name: 'dashboard_page_route',
            params: {
              id: element.pivot.template_dashboard_id
            }
          })
          this.$h.toastSuccess(
            `${element.name[this.$i18n.locale]} ${this.$t(
              'global.add_to_favorite'
            )}`
          )
        } else {
          if (this.favoriteUserDashboards[0]) {
            this.$router.push({
              name: 'dashboard_page_route',
              params: {
                id: this.favoriteUserDashboards[0].id
              }
            })
          } else {
            this.$router.push({
              name: 'dashboard_page_route',
              params: {
                id: this.templateDashboard[0].id
              }
            })
          }
          this.$h.toastSuccess(
            `${element.name[this.$i18n.locale]} ${this.$t(
              'global.not_add_to_favorite'
            )}`
          )
        }
      })
    }
  }
}
</script>
